import { useGetBlockedUserByIdQuery } from '@exam-center/graphql';
import { Navigate } from '@exam-center/router';
import { Loading } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { createContext, useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import type { BlockedUserDetailsContextValue, BlockedUserDetailsProviderProps } from './types';

const BlockedUserDetailsContext = createContext<BlockedUserDetailsContextValue | undefined>(
  undefined,
);

export default function BlockedUserDetailsProvider(
  props: Readonly<BlockedUserDetailsProviderProps>,
) {
  const { blockedUserId, children } = props;
  const { showSnackbar } = useSnackbar();

  const { data: getBlockedUserResult, loading: gettingBlockedUser } = useGetBlockedUserByIdQuery({
    variables: {
      input: {
        id: blockedUserId,
      },
    },
  });

  const blockedUserDetails = useMemo(() => {
    if (!getBlockedUserResult) {
      return null;
    }

    return getBlockedUserResult?.ecGetBlockedUserById.blockedUser ?? null;
  }, [getBlockedUserResult]);

  const contextValue: BlockedUserDetailsContextValue = useMemo(() => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      blockedUserDetails: blockedUserDetails!,
    };
  }, [blockedUserDetails]);

  useEffect(() => {
    if (!blockedUserDetails && !gettingBlockedUser) {
      showSnackbar({
        message: 'ไม่พบข้อมูลรายชื่อผู้ถูกระงับสิทธิ์ดังกล่าว',
        variant: 'muted',
      });
    }
  }, [blockedUserDetails, gettingBlockedUser, showSnackbar]);

  if (gettingBlockedUser) {
    return <Loading height='100%' />;
  }

  if (!blockedUserDetails) {
    return <Navigate to='/admin/blocklist' />;
  }

  return (
    <BlockedUserDetailsContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </BlockedUserDetailsContext.Provider>
  );
}

export { BlockedUserDetailsContext };
